import { parsePriceString } from '@/components/setup/setupManualSchemas';
import type { ProductItem } from '@/lib/api/products';

export interface SetupCatalogSummary {
  productCount: number;
  variantCount: number;
  previewProductName: string;
  previewProductImageUrl: string;
  previewProductPrice: string;
  lastProductVariantCount: number;
}

function formatCatalogPrice(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / 100);
}

function pickPreviewVariant(product: ProductItem) {
  const variants = product.variants ?? [];
  return variants.find((variant) => variant.default) ?? variants[0];
}

function pickPreviewPrice(variant: NonNullable<ReturnType<typeof pickPreviewVariant>>, currency: string) {
  const prices = variant.prices ?? [];
  return (
    prices.find((price) => price.default) ??
    prices.find((price) => price.currencyOptions?.[currency]) ??
    prices[0]
  );
}

/** Max products loaded for setup checkout preview / validation. */
export const SETUP_CATALOG_LIST_PAGE_SIZE = 500;

/** products.list uses 0-based page index — never pass page: 1 for the first page. */
export function buildSetupProductsListInput(
  storeId: string,
  perPage = SETUP_CATALOG_LIST_PAGE_SIZE,
) {
  return {
    storeId,
    page: 0,
    per_page: perPage,
    includeVariants: true as const,
    sort: 'created_at' as const,
    sort_order: 'desc' as const,
  };
}

/** Aggregate catalog stats + latest product preview for setup aside. */
export function buildSetupCatalogSummary(
  products: ProductItem[] | undefined,
  currency: string,
): SetupCatalogSummary | null {
  if (!products?.length) return null;

  const activeProducts = products.filter((product) => product.active !== false);
  if (!activeProducts.length) return null;

  let variantCount = 0;
  for (const product of activeProducts) {
    variantCount += (product.variants ?? []).length;
  }

  const previewProduct = activeProducts[0];
  const previewVariant = pickPreviewVariant(previewProduct);
  const previewPrice = previewVariant ? pickPreviewPrice(previewVariant, currency) : undefined;
  const amountMinor = previewPrice?.currencyOptions?.[currency]?.amount;
  const lastProductVariantCount = (previewProduct.variants ?? []).length;

  return {
    productCount: activeProducts.length,
    variantCount,
    previewProductName: previewProduct.name?.trim() ?? '',
    previewProductImageUrl: previewVariant?.imageUrl?.trim() ?? '',
    previewProductPrice:
      amountMinor != null && amountMinor > 0 ? formatCatalogPrice(amountMinor, currency) : '',
    lastProductVariantCount,
  };
}

export function mergeSetupCatalogAfterCreate(
  existing: SetupCatalogSummary | null | undefined,
  productName: string,
  variants: Array<{ imageUrl?: string; priceAmount?: string }>,
  currency: string,
): SetupCatalogSummary {
  const firstVariant = variants[0];
  const amount = firstVariant?.priceAmount ? parsePriceString(firstVariant.priceAmount) : null;

  return {
    productCount: (existing?.productCount ?? 0) + 1,
    variantCount: (existing?.variantCount ?? 0) + variants.length,
    previewProductName: productName.trim(),
    previewProductImageUrl: firstVariant?.imageUrl?.trim() ?? '',
    previewProductPrice:
      amount !== null && amount > 0 ? formatCatalogPrice(Math.round(amount * 100), currency) : '',
    lastProductVariantCount: variants.length,
  };
}
